/**
 * Video Registry — single source of truth for Szkoła Ofertatora tutorial videos.
 *
 * SOLID Principles:
 * - Open/Closed: add new videos here; no other file needs to change for the School page.
 * - ISP: kept separate from MODULE_REGISTRY — videos have no routing/permission concerns.
 * - Separate from onboardingRegistry.videoId — guide step clips ≠ School catalog.
 *
 * relatedModuleId (optional): when set, pages for that module can query and display
 * a VideoTutorialButton via getVideosByModuleId().
 *
 * locales (optional): when set, video is only shown for those UI languages.
 * Omit = all locales. Current catalog is Polish-only.
 */

import type { Locale } from '../i18n/locales'
import { isContentLocaleAllowed } from '../lib/contentLocale'

export interface TutorialVideo {
  id: string
  title: string
  youtubeId: string
  /** Module page where this video should surface as a quick-access button */
  relatedModuleId?: string
  order: number
  /** Locales where this video is shown. Omit = all locales. */
  locales?: readonly Locale[]
}

export const VIDEO_REGISTRY: TutorialVideo[] = [
  {
    id: 'ofertator_1',
    title: 'Ofertator #1 - Jak podłączyć swoje pierwsze konto Allegro do aplikacji',
    youtubeId: 'cjk7QStWFjM',
    relatedModuleId: 'konta_marketplace',
    order: 1,
    locales: ['pl'],
  },
  {
    id: 'ofertator_2',
    title: 'Ofertator #2 - Opisomat, pierwszy prosty sposób generowania opisów na Allegro',
    youtubeId: 'mN2Kwm1r8Lo',
    relatedModuleId: 'allegro_opisomat',
    order: 2,
    locales: ['pl'],
  },
  {
    id: 'ofertator_3',
    title: 'Grafikomat 1.0, generowanie zdjęć produktów Allegro',
    youtubeId: 'GvY_0XDmURg',
    relatedModuleId: 'allegro_grafikomat',
    order: 3,
    locales: ['pl'],
  },
  {
    id: 'ofertator_4',
    title: 'Jak wystawić 300 dopracowanych ofert na Allegro w 15 minut?',
    youtubeId: 'bH1ji1FE-BQ',
    relatedModuleId: 'allegro_wystawianie_od_zera',
    order: 4,
    locales: ['pl'],
  },
]

const byOrder = (a: TutorialVideo, b: TutorialVideo) => a.order - b.order

const availableIn = (locale: string) => (v: TutorialVideo) =>
  isContentLocaleAllowed(v.locales, locale)

export const getVideosByModuleId = (moduleId: string, locale: string): TutorialVideo[] =>
  VIDEO_REGISTRY.filter((v) => v.relatedModuleId === moduleId)
    .filter(availableIn(locale))
    .sort(byOrder)

export const getAllVideos = (locale: string): TutorialVideo[] =>
  VIDEO_REGISTRY.filter(availableIn(locale)).sort(byOrder)

export const hasVideosForLocale = (locale: string): boolean =>
  VIDEO_REGISTRY.some(availableIn(locale))
